/**
 *
 * @param {string[]} primaryStudyInstanceUIDs
 * @param {object[]} studyDisplayList
 * @param {string} studyDisplayList.studyInstanceUid
 * @param {string} studyDisplayList.date
 * @param {string} studyDisplayList.description
 * @param {string} studyDisplayList.modalities
 * @param {number} studyDisplayList.numInstances
 * @param {object[]} displaySets
 * @param {object} displaySetService - The display set service instance
 * @param {number} recentTimeframe - The number of milliseconds to consider a study recent
 * @returns tabs - The prop object expected by the StudyBrowser component
 */

export function createStudyBrowserTabs(
  primaryStudyInstanceUIDs,
  studyDisplayList,
  displaySets,
  displaySetService,
  recentTimeframeMS = 31536000000
) {

  const shouldSortBySeriesUID = process.env.TEST_ENV === 'true';
  const primaryStudies = [];
  const allStudies = [];

  studyDisplayList.forEach(study => {
    const displaySetsForStudy = displaySets.filter(
      ds => ds.StudyInstanceUID === study.studyInstanceUid
    );

    // sort them by seriesNumber or seriesInstanceUID
    let sortedDisplaySets;
    if (shouldSortBySeriesUID) {
      sortedDisplaySets = displaySetsForStudy.sort((a, b) => {
        const displaySetA = displaySetService.getDisplaySetByUID(a.displaySetInstanceUID);
        const displaySetB = displaySetService.getDisplaySetByUID(b.displaySetInstanceUID);

        return displaySetA.SeriesInstanceUID.localeCompare(displaySetB.SeriesInstanceUID);
      });
    } else {
      // Sort by series number in ascending order
      sortedDisplaySets = displaySetsForStudy.sort((a, b) => {
        const seriesNumberA = parseInt(a.seriesNumber) || 0;
        const seriesNumberB = parseInt(b.seriesNumber) || 0;
        return seriesNumberA - seriesNumberB;
      });
    }

    const tabStudy = Object.assign({}, study, {
      displaySets: sortedDisplaySets,
    });

    if (primaryStudyInstanceUIDs.includes(study.studyInstanceUid)) {
      primaryStudies.push(tabStudy);
    }
    allStudies.push(tabStudy);
  });

  const primaryStudiesTimestamps = primaryStudies
    .filter(study => study.date)
    .map(study => new Date(study.date).getTime());

  const recentStudies =
    primaryStudiesTimestamps.length > 0
      ? allStudies.filter(study => {
          const oldestPrimaryTimeStamp = Math.min(...primaryStudiesTimestamps);

          if (!study.date) {
            return false;
          }
          const studyTimeStamp = new Date(study.date).getTime();
          return oldestPrimaryTimeStamp - studyTimeStamp < recentTimeframeMS;
        })
      : [];

  // Newest first
  const _byDate = (a, b) => {
    const dateA = Date.parse(a);
    const dateB = Date.parse(b);

    return dateB - dateA;
  };

  const tabs = [
    {
      name: 'primary',
      label: 'Primary',
      studies: primaryStudies.sort((studyA, studyB) => _byDate(studyA.date, studyB.date)),
    },
    {
      name: 'recent',
      label: 'Recent',
      studies: recentStudies.sort((studyA, studyB) => _byDate(studyA.date, studyB.date)),
    },
    {
      name: 'all',
      label: 'All',
      studies: allStudies.sort((studyA, studyB) => _byDate(studyA.date, studyB.date)),
    },
  ];

  return tabs;
}

/**
 * Creates worklist tabs for recent studies (worklist mode)
 * @param {object[]} worklistStudies - Array of study objects with basic metadata and displaySets
 * @returns tabs - Single "Worklist" tab with all studies
 */
export function createWorklistTabs(worklistStudies) {
  // Studies are grouped by body part in a fixed clinical order. The body part
  // is encoded in the study description / patient name as "<BodyPart>-<n>"
  // (e.g. "Liver-10", "Female pelvis-3", "Non-Recurrence-1"), so the body-part
  // key is the label with the trailing "-<number>" suffix stripped.
  const _BODY_PART_ORDER = [
    'liver',
    'pancreas',
    'spleen',
    'renal',
    'adrenals',
    'ibd',
    'female pelvis',
    'recurrence',
    'non-recurrence',
    'rectal cancer'
  ];

  // Prefer the description; fall back to patient name (stripping a leading
  // "Patient " prefix) so both label styles resolve to the same body part.
  const _bodyPartKey = study => {
    const raw = (study.description || study.patientName || '')
      .replace(/^patient\s+/i, '')
      .trim();
    return raw.replace(/-[a-zA-Z]*\d+.*$/, '').trim().toLowerCase();
  };

  // Unknown body parts sort after every known one, but before blanks.
  const _bodyPartRank = study => {
    const key = _bodyPartKey(study);
    if (!key) {
      return _BODY_PART_ORDER.length + 1;
    }
    const index = _BODY_PART_ORDER.indexOf(key);
    return index === -1 ? _BODY_PART_ORDER.length : index;
  };

  const _byBodyPart = (studyA, studyB) => {
    const rankDiff = _bodyPartRank(studyA) - _bodyPartRank(studyB);
    if (rankDiff !== 0) {
      return rankDiff;
    }

    // Within the same body part, natural sort by label so "-2" lands between
    // "-1" and "-10" instead of after "-19".
    const labelA = (studyA.description || studyA.patientName || '').trim();
    const labelB = (studyB.description || studyB.patientName || '').trim();
    return labelA.localeCompare(labelB, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Placeholder rows the archive carries for anonymised/test data
  // (DummyPatName!, DummyStudyDesc!) are not real work — hide them.
  const _isPlaceholder = study =>
    /dummy/i.test(study.patientName || '') || /dummy/i.test(study.description || '');

  // Keep displaySets if they exist (for currently loaded study), otherwise empty array
  const studiesWithDisplaySets = worklistStudies
    .filter(study => !_isPlaceholder(study))
    .map(study => ({
      ...study,
      displaySets: study.displaySets || [],
    }));

  const tabs = [
    {
      name: 'worklist',
      label: 'Studies',
      studies: studiesWithDisplaySets.sort(_byBodyPart),
    },
  ];

  return tabs;
}
